/**
 * Typed client for the Creative Reading backend.
 *
 * The contract is documented in the project research under
 * "API and Schema Documentation":
 *   - SSE: one EventSource per book multiplexing `page_text`, `image_ready`,
 *     and `book_complete` events.
 *   - REST: /api/books, /api/children, /api/attempts plus guardian export and
 *     delete, all behind an HttpOnly signed-cookie session.
 *
 * All requests send credentials so the session cookie rides along. The backend
 * fronts the SPA on the same Caddy origin in production, so requests are
 * same-origin; the Vite dev proxy maps /api and /books to the FastAPI server.
 */

export const API_BASE = "/api";

/** A child profile (pseudonymous nickname only — Loi 25 data minimisation). */
export interface Child {
  id: string;
  displayName: string;
  avatar: string;
  readingLevel: string;
}

/**
 * The canonical RAZ reading-band ladder, lowest to highest. Mirrors
 * `RAZ_LADDER` in `api/adaptive/ladder.py`; the two must stay consistent.
 * Surfaced in the guardian dashboard so a grown-up can manually set a child's
 * level to preview harder books and questions.
 */
export const READING_LEVELS = [
  "aa",
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
  "Z1",
  "Z2",
] as const;

/** Reading band ladder position, surfaced on the guardian dashboard. */
export interface MasteryView {
  childId: string;
  level: string;
  recentAccuracy: number;
  attemptsAtLevel: number;
  lastChangedAt: string | null;
  /** Past quiz scores (0..1) in chronological order, oldest first. */
  scoreHistory: number[];
}

/** A single rendered book page. */
export interface BookPage {
  pageNumber: number;
  text: string;
  imageUrl: string | null;
  imageWidth: number;
  imageHeight: number;
  /**
   * Whether this page is meant to carry an illustration under the level-scaled
   * cadence. Text-only pages render with no image slot; an expected-but-missing
   * image shows a calm "picture coming soon" placeholder a grown-up can fix.
   */
  expectsImage?: boolean;
}

/** Book list/summary item. */
export interface BookSummary {
  id: string;
  title: string;
  version: number;
  level: string;
  /** Whether this is a make-believe story or a true non-fiction reader. */
  bookType: "fiction" | "nonfiction";
  coverUrl: string | null;
  createdAt: string;
  pageCount: number;
}

/** A fully materialised book (text JSON the SW caches for offline re-reading). */
export interface Book extends BookSummary {
  contentUrl: string;
  pages: BookPage[];
}

/** Comprehension question presented in the Q&A flow. */
export interface Question {
  id: string;
  pageNumber: number;
  prompt: string;
  kind: "multiple_choice" | "free_text";
  choices: string[];
}

/** A graded attempt result returned after submission. */
export interface AttemptResult {
  attemptId: string;
  score: number;
  correct: boolean;
  encouragement: string;
  feedback: string;
  /** The child's reading band after this attempt (may have advanced). */
  readingLevel: string;
}

/** Payload queued in the offline outbox and replayed on reconnect. */
export interface AttemptSubmission {
  childId: string;
  bookId: string;
  questionId: string;
  answer: string;
  /** Client clock at attempt time; the server records authoritative time. */
  clientTimestamp: string;
}

export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
    ...init,
  });

  if (!response.ok) {
    throw new ApiError(
      `Request to ${path} failed`,
      response.status,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/* ----------------------------- Auth ----------------------------- */

export interface SessionInfo {
  childId: string | null;
  guardianUnlocked: boolean;
}

export const auth = {
  /** Current session (which child is signed in, guardian PIN state). */
  session: () => request<SessionInfo>("/auth/session"),
  /** Passwordless kid sign-in via avatar pick. */
  selectChild: (childId: string) =>
    request<SessionInfo>("/auth/child", {
      method: "POST",
      body: JSON.stringify({ childId }),
    }),
  /** Unlock the guardian dashboard with the 4-digit PIN. */
  unlockGuardian: (pin: string) =>
    request<SessionInfo>("/auth/guardian", {
      method: "POST",
      body: JSON.stringify({ pin }),
    }),
  signOut: () => request<void>("/auth/signout", { method: "POST" }),
};

/* ---------------------------- Children -------------------------- */

export interface CreateChildRequest {
  nickname: string;
  avatar: string;
  readingLevel?: string;
}

export const children = {
  list: () => request<Child[]>("/children"),
  mastery: (childId: string) =>
    request<MasteryView>(`/children/${childId}/mastery`),
  create: (body: CreateChildRequest) =>
    request<Child>("/children", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /**
   * Grown-up only: patch a child profile. Used by the guardian dashboard to
   * manually set a reading level for testing harder books and questions.
   */
  update: (childId: string, body: Partial<CreateChildRequest>) =>
    request<Child>(`/children/${childId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
};

/* ----------------------------- Books ---------------------------- */

export interface CreateBookRequest {
  childId: string;
  prompt: string;
  /** Make-believe story or a true non-fiction reader. Defaults to fiction. */
  bookType?: "fiction" | "nonfiction";
  /** Optional explicit harder/easier nudge from the Q&A flow. */
  difficultyNudge?: "harder" | "easier";
}

export interface CreateBookResponse {
  bookId: string;
  /** EventSource URL for the streaming generation of this book. */
  streamUrl: string;
}

export interface DeleteIncompleteResult {
  /** Number of unfinished books that were removed. */
  deleted: number;
  /** Titles of the removed books, for a friendly grown-up confirmation. */
  titles: string[];
}

export interface FixMissingImagesResult {
  /** Complete books that were scanned for missing illustrations. */
  booksScanned: number;
  /** Page illustrations that were successfully backfilled. */
  pagesFixed: number;
  /** Pages whose illustration could not be regenerated this run. */
  pagesFailed: number;
}

/** A live progress update while missing pictures are being redrawn. */
export interface FixMissingImagesProgress {
  /** "working" before a draw starts, "ok" / "failed" once it finishes. */
  status: "working" | "ok" | "failed";
  /** Title of the book whose page is currently being drawn. */
  book: string;
  /** Page number being drawn. */
  page: number;
  /** 1-based position of this page within the whole sweep. */
  index: number;
  /** Total number of missing pictures to draw this run. */
  total: number;
  /** Pictures drawn so far. */
  fixed: number;
  /** Pictures that could not be drawn so far. */
  failed: number;
}

export const books = {
  list: (childId?: string) =>
    request<BookSummary[]>(
      childId ? `/books?childId=${encodeURIComponent(childId)}` : "/books",
    ),
  get: (bookId: string) => request<Book>(`/books/${bookId}`),
  /** Kick off generation; consume the returned streamUrl with useBookStream. */
  create: (body: CreateBookRequest) =>
    request<CreateBookResponse>("/books", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Stable SSE URL for an in-progress book. */
  streamUrl: (bookId: string) => `${API_BASE}/books/${bookId}/stream`,
  questions: (bookId: string) =>
    request<Question[]>(`/books/${bookId}/questions`),
  /**
   * Grown-up only: soft-delete every unfinished book across this guardian's
   * children. Requires an unlocked guardian session (401 otherwise).
   */
  deleteIncomplete: () =>
    request<DeleteIncompleteResult>("/books/incomplete", { method: "DELETE" }),
  /**
   * Grown-up only: backfill illustrations that the level-scaled cadence expects
   * but are missing (e.g. a page whose image failed to generate). Pages that
   * are intentionally text-only are left untouched. Requires an unlocked
   * guardian session (401 otherwise).
   *
   * Each picture takes tens of seconds to draw, so the backend streams progress
   * as Server-Sent Events. The optional `onProgress` callback fires for every
   * `fix_progress` event so the UI can show which book and page are drawing and
   * an overall count; the promise resolves with the final summary.
   */
  fixMissingImages: (
    onProgress?: (progress: FixMissingImagesProgress) => void,
  ): Promise<FixMissingImagesResult> =>
    streamFixMissingImages(onProgress),
};

/**
 * Open the streaming fix-missing-pictures sweep and dispatch its SSE events.
 *
 * Uses `fetch` (not `EventSource`, which is GET-only) so the same signed-cookie
 * POST contract as the other grown-up controls applies. Parses the
 * `text/event-stream` body incrementally, invoking `onProgress` per
 * `fix_progress` event and resolving with the `fix_complete` summary.
 */
async function streamFixMissingImages(
  onProgress?: (progress: FixMissingImagesProgress) => void,
): Promise<FixMissingImagesResult> {
  const response = await fetch(`${API_BASE}/books/fix-missing-images`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "text/event-stream" },
  });
  if (!response.ok || !response.body) {
    throw new ApiError("Request to /books/fix-missing-images failed", response.status);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let summary: FixMissingImagesResult | null = null;

  const handleBlock = (block: string) => {
    let event = "message";
    let data = "";
    for (const line of block.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) data += line.slice(5).trim();
    }
    if (!data) return;
    const payload = JSON.parse(data) as Record<string, unknown>;
    if (event === "fix_progress") {
      onProgress?.(payload as unknown as FixMissingImagesProgress);
    } else if (event === "fix_complete") {
      summary = payload as unknown as FixMissingImagesResult;
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (value) buffer += decoder.decode(value, { stream: true });
    let sep = buffer.indexOf("\n\n");
    while (sep !== -1) {
      handleBlock(buffer.slice(0, sep));
      buffer = buffer.slice(sep + 2);
      sep = buffer.indexOf("\n\n");
    }
    if (done) break;
  }
  if (buffer.trim()) handleBlock(buffer);

  return summary ?? { booksScanned: 0, pagesFixed: 0, pagesFailed: 0 };
}

/* ---------------------------- Feedback -------------------------- */

export interface FeedbackResult {
  /** "created" when a GitHub issue was filed, "queued_locally" when the
   *  backend has no GitHub token configured. */
  status: "created" | "queued_locally";
  issueUrl: string | null;
  issueNumber: number | null;
}

export const feedback = {
  /**
   * Grown-up only: file a text-only bug/feature issue. Requires an unlocked
   * guardian session (401 otherwise). No-ops to "queued_locally" when the
   * backend has no GitHub token.
   */
  create: (body: { kind: "bug" | "feature"; title: string; body: string }) =>
    request<FeedbackResult>("/feedback", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};

/* ---------------------------- Attempts -------------------------- */

export const attempts = {
  /**
   * Submit a graded attempt. Throws on network failure so callers can fall
   * back to the offline outbox; the body shape matches the outbox payload.
   */
  submit: (body: AttemptSubmission) =>
    request<AttemptResult>("/attempts", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};

/* ---------------------------- Guardian -------------------------- */

export const guardian = {
  /** Synchronous export of all data for Loi 25 access requests. */
  exportAllUrl: () => `${API_BASE}/guardian/export`,
  /** Cascade delete of all children's data (Loi 25 erasure, 30-day window). */
  deleteAll: () =>
    request<void>("/guardian/data", { method: "DELETE" }),
};
